<template>
  <div class="AddressesBox">
    <div class="AddressesBox__delivery">
      <AddressCard
        v-if="displayDeliveryAddress"
        label="Adresse de livraison"
        :address="selectedAddress"
        :canEdit="true"
        @editAddress="editAddress"
      />

      <div class="AddressSelect" v-if="addresses.length > 1 && displaySelect">
        <div class="AddressSelect__label">Selectionnez une adresse de livraison: </div>
        <div class="AddressSelect__select">
          <select v-model="localSelectedAddress" class="Select Select--variant Select--small">
            <option
              v-for="address in addresses"
              :key="address.id"
              :value="address"
            >
              {{ address.name }} - {{ address.address }}, {{ address.city }}
            </option>
          </select>
        </div>
        <div class="AddressSelect__footer">
          <div class="AddressSelect__new" @click="showForm">Nouvelle adresse</div>
          <div class="AddressSelect__cancel Button Button--small" @click="cancelSelectAddress">Annuler</div>
          <div class="AddressSelect__confirm Button Button--small Button--primary" @click="selectAddress">Confirmer</div>
        </div>
      </div>
      <AddressForm
        :title="`Créer une ${addresses.length > 0 ? 'nouvelle ' : '' } adresse de livraison: `"
        :addresses="addresses"
        v-if="addresses.length === 0 || displayDeliveryForm"
        v-model="newDeliveryAddress"
        @save="addNewDeliveryAddress"
        @cancel="cancelDeliveryForm"
      />
      <div
        class="AddressesBox__edit-billing"
        v-if="hasSameAddresses || !user.billing_address"
      >
        <span @click="displayBillingForm = true">Choisir une adresse de facturation différente</span>
      </div>
    </div>
    <div class="AddressesBox__billing">
      <AddressCard
        v-if="displayBillingAddress"
        label="Adresse de facturation"
        :address="user.billing_address"
        :canEdit="true"
        @editAddress="displayBillingForm = true"
      />
      <div v-if="displayBillingForm">
        <AddressForm
          title="Modifier mon adresse de facturation: "
          v-model="newBillingAddress"
          @save="updateBillingAddress"
          @cancel="cancelBillingForm"
        >
          <div class="AddressesBox__disclaimer">
            Attention, cette modification sera appliquée sur l’ensemble de vos commandes CaptainVin, passées et futures.
          </div>
        </AddressForm>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import notificationService from "../services/notification";
import AddressCard from "./AddressCard";
import AddressForm from "./AddressForm";

const initialAddress = {
  address: "",
  city: "",
  zipcode: "",
  country: "FR",
  additional_info: "",
  name: ""
};

export default {
  props: {
    addresses: {
      type: Array,
      required: true
    },
    selectedAddress: {
      validator: value => value === null || typeof value === "object",
      default: null
    }
  },
  data() {
    return {
      localSelectedAddress: null,
      isLoading: false,
      displaySelect: false,
      displayDeliveryForm: false,
      displayBillingForm: false,
      newDeliveryAddress: null,
      newBillingAddress: null
    };
  },
  computed: {
    ...mapState("auth", ["user"]),
    displayDeliveryAddress() {
      return this.localSelectedAddress && !this.displaySelect && !this.displayDeliveryForm;
    },
    hasSameAddresses() {
      return this.selectedAddress && this.selectedAddress.id === this.user.billing_address?.id;
    },
    displayBillingAddress() {
      return !this.displayBillingForm && !this.hasSameAddresses;
    }
  },
  watch: {
    selectedAddress: {
      handler() {
        this.localSelectedAddress = this.selectedAddress;
      },
      immediate: true
    }
  },
  methods: {
    editAddress() {
      if (this.addresses.length > 1) {
        this.showSelect();
      } else {
        this.showForm();
      }
    },
    cancelSelectAddress() {
      this.localSelectedAddress = this.selectedAddress;
      this.showAddress();
    },
    showAddress() {
      this.displaySelect = false;
      this.displayDeliveryForm = false;
    },
    showForm() {
      this.displaySelect = false;
      this.displayDeliveryForm = true;
    },
    showSelect() {
      this.displaySelect = true;
      this.displayDeliveryForm = false;
    },
    addNewDeliveryAddress() {
      this.$emit("addAddress", this.newDeliveryAddress);
      this.cancelDeliveryForm();
    },
    selectAddress(a) {
      this.$emit("selectAddress", this.localSelectedAddress);
      this.showAddress();
    },
    updateBillingAddress() {
      if (this.isLoading) {
        return;
      }

      this.isLoading = true;

      this.$store.dispatch("auth/updateBillingAddress", this.newBillingAddress)
        .then(this.cancelBillingForm)
        .catch(this.onError)
        .finally(() => this.isLoading = false);
    },
    cancelBillingForm() {
      this.displayBillingForm = false;
      this.newBillingAddress = { ...initialAddress, name: this.user.name };
    },
    cancelDeliveryForm() {
      this.displayDeliveryForm = false;
      this.newDeliveryAddress = { ...initialAddress, name: this.user.name };
    },
    onError(err) {
      notificationService.error(err.message, err.errors);
    }
  },
  created() {
    this.newBillingAddress = { ...initialAddress, name: this.user.name };
    this.newDeliveryAddress = { ...initialAddress, name: this.user.name };
  },
  components: {
    AddressCard,
    AddressForm
  }
};
</script>

<style lang="scss" scoped>
@import '@/assets/_variables.scss';

.AddressSelect {
  &__label {
    font-size: 0.8rem;
    margin-bottom: $global-margin;
  }

  &__select {
    margin-bottom: $global-margin;
  }

  &__new {
    margin-right: auto;
    font-size: 0.8rem;
    text-decoration: underline;
  }

  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    .Button {
      margin-left: $global-margin;
    }
  }
}

.AddressesBox {
  display: flex;
  justify-content: space-between;
  margin-bottom: $global-margin;

  &__disclaimer {
    margin-bottom: $global-margin;
    font-style: italic;
    color: $color-secondary;
    font-size: 0.8rem;
  }

  &__delivery,
  &__billing {
    width: calc(50% - #{$global-margin});
    flex-shrink: 0;
  }

  &__edit-billing {
    font-size: 0.8em;
    text-decoration: underline;
    margin-top: $global-margin;

    span {
      cursor: pointer;
    }
  }
}

@media (max-width: 900px) {
  .AddressesBox {
    display: block;

    &__delivery,
    &__billing {
      width: 100%;
      margin-bottom: $global-margin * 2;
    }
  }
}

</style>
